/**
 * `events.ts` — **nama peristiwa outbox + payload bertipe** (kontrak tiga lapis: portal ↔ outlet ↔ klien).
 *
 * Sumber: `design/SCHEMA-v0.md` §2 · §3 · §4 · §4b · §5 · §6 · §7 · §8 · §9 · §10 · §10b ·
 * `research/SYNC-ARCHITECTURE.md` §1 · §3 · §4 · §4b · §5 ·
 * `research/DESIGN-order-lifecycle.md` §2 · §4 · §5b · `CLAUDE.md` K5 · K7 · K10 · K11 · K12.
 *
 * ---
 * ### ⛔ Batas cakupan — apa yang berkas ini BUKAN
 *
 * | Bukan | Rumahnya |
 * |---|---|
 * | Replikasi record (order/tender/stock naik ke portal) | `sync.ts` — status ack = **atribut RECORD** (P7, §10) |
 * | Jejak audit per baris pesanan | tabel `order_line_event` append-only (§10, DESIGN §2) |
 * | Mesin hitung pajak / mesin poin | ditolak eksplisit (K11/K12, §12) — di sini pajak hanya **snapshot hasil** |
 * | Klien HTTP / pengiriman webhook | `webhook_delivery` (§10b) — di sini hanya **bentuknya** |
 *
 * ⚠️ **Kenapa batas ini keras.** VTEC menulis pembukuan sinkronisasi per (batch × kunci) ⇒
 * `Log_TranKeyFront` **86.441.640 baris untuk 3.276.190 transaksi = 26× per transaksi**
 * (SYNC §3). Kalau outbox ikut membawa seluruh isi pesanan, kita mengulang persis kesalahan itu.
 * ⇒ **Outbox = fakta ekonomi tingkat bill; detail baris diambil konsumen lewat record sync.**
 *
 * ---
 * ### Konvensi penamaan: `<agregat>.<lampau>`
 *
 * ```
 * ^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$      (tepat satu titik, ASCII, ≤40 char)
 * order.paid · shift.opened · stock.moved · business_day.closed
 * ```
 *
 * - Segmen 1 = **agregat** (pemilik fakta, R3 §10b): satu fakta satu pemilik.
 * - Segmen 2 = **kata kerja LAMPAU**: peristiwa = fakta yang **sudah terjadi**. Nama imperatif
 *   (`order.pay`) mengundang konsumen merasa boleh menolak/menahan ⇒ menabrak **R1 §10b**:
 *   *companion tak pernah boleh memblokir penjualan*.
 *
 * ### ⭐ Kenapa nama peristiwa STABIL SELAMANYA
 * 1. Nama tersimpan **di data**, bukan hanya di kode: `webhook_endpoint.events[]` (§10b) dan baris
 *    outbox yang bisa diputar ulang berhari-hari kemudian setelah outlet luring (SYNC §5).
 * 2. Nama adalah kontrak tiga lapis **plus** kontrak ke companion pihak ketiga yang siklus
 *    rilisnya bukan milik kita.
 * 3. Kegagalan akibat rename bersifat **senyap** — konsumen berhenti menerima, dan itu terlihat
 *    persis seperti "hari ini sepi". Bukti pola ini nyata: `RewardPointHistory` Arena **4.547 baris
 *    belum tersinkron + 719 error, bertahun-tahun, tanpa ada yang tahu** (R2 §10b).
 *
 * ⇒ **Daftar nama bersifat append-only** (P5 dalam bentuk lain). Nama tak pernah di-rename, tak
 * pernah dipakai ulang untuk makna lain. Menghentikan peristiwa = berhenti memancarkan, namanya
 * tetap **dicadangkan** ({@link RESERVED_EVENT_NAMES}). Perubahan bentuk payload ditangani
 * {@link EVENTS_SCHEMA_VERSION}, **bukan** nama baru.
 *
 * ---
 * ### Retensi (P9) — outbox BUKAN arsip
 * Baris outbox dihapus/dipadatkan setelah ack (SYNC §3: *"status sinkron adalah ATRIBUT dari
 * record"*). Jejak audit adalah `order_event`/`order_line_event` (§10) dan **disimpan penuh**.
 * Jangan pernah menjadikan outbox tempat menyimpan sejarah.
 *
 * ### Nol dependensi runtime
 * Hanya modul kontrak lain (`./money.js`, `./tender.js`, `./tax.js`). Tak ada `zod`/`decimal.js`.
 * Semua nilai uang/kuantitas/tarif di kawat = **string desimal kanonik**, tak pernah `number` (P1).
 *
 * @module
 */
import { addMoney, eqMoney, isMoneyString, isQtyString, isRateString, isZeroQty, subMoney, sumMoney, tryMoney, tryQty, } from './money.js';
import { isCardLast4, isEdcMode, isTenderType } from './tender.js';
import { isTaxKind } from './tax.js';
/* ────────────────────────────────────────────────────────────────────────────
 * 2. Tata bahasa & konstruktor branded
 * ──────────────────────────────────────────────────────────────────────────── */
/** §2 `CODE` = `varchar(40)`. Digit/huruf ASCII + `.`/`_`/`-`, tanpa spasi di mana pun. */
const CODE_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,39}$/;
/** §3 `order_no` = `<outlet_code>/<yymmdd>/<urut>`. */
const ORDER_NO_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,39}\/[0-9]{6}\/[0-9]{1,10}$/;
/**
 * UUID **versi 7** (§3, SYNC §1). Nibble versi wajib `7` dan nibble varian wajib `8|9|a|b`.
 * Huruf kecil saja: `event_id` adalah **kunci dedupe** (SYNC §3) ⇒ perbandingannya berbasis string,
 * jadi bentuknya harus kanonik byte-per-byte, bukan "sama kalau di-lowercase dulu".
 */
const UUID_V7_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
/** §2 `TS` — UTC berakhiran `Z`; pecahan detik opsional 1..9 digit. */
const TS_RE = /^([0-9]{4})-([0-9]{2})-([0-9]{2})T([0-9]{2}):([0-9]{2}):([0-9]{2})(?:\.[0-9]{1,9})?Z$/;
/** §2 `BDATE` — `YYYY-MM-DD` polos. */
const BDATE_RE = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/;
/** Nomor approval EDC (§7/K7) — sama batasnya dengan `tender.ts`. */
const APPROVAL_CODE_RE = /^[A-Za-z0-9]{1,20}$/;
/** Batas panjang teks bebas terkendali (`reason_note`, snapshot nama). */
const TEXT_MAX = 200;
/** Batas panjang nomor acuan/batch — cukup untuk QRIS/EDC, cukup sempit untuk menolak sampah. */
const REFERENCE_MAX = 64;
const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
function isLeapYear(y) {
    return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}
/** Validasi kalender nyata — `2026-02-30` ditolak, bukan diam-diam digeser seperti `new Date()`. */
function isValidYmd(y, m, d) {
    if (m < 1 || m > 12 || d < 1)
        return false;
    const base = MONTH_DAYS[m - 1];
    if (base === undefined)
        return false;
    const max = m === 2 && isLeapYear(y) ? 29 : base;
    return d <= max;
}
/**
 * ⭐ **P10 sebagai kontrol yang MEMAKSA, bukan komentar.**
 *
 * P10 lahir dari bukti: **680 terminal VTEC dengan kolom IP terisi NOL** (§0/§8i). Kalau
 * `device_code` boleh berisi alamat, ia **akan** berisi alamat — dan alamat berubah tiap DHCP
 * sementara identitas tidak (K6: printer pun dialamati identitas + IP statis, bukan state OS).
 *
 * Menangkap: dotted-quad IPv4 (`192.168.2.115`), IPv4 + port (`10.0.0.7:9100`), dan bentuk IPv6
 * (≥2 titik dua, hanya hex/titik/titik-dua).
 */
export function looksLikeIpAddress(value) {
    const bare = value.trim();
    if (/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}(?::[0-9]{1,5})?$/.test(bare))
        return true;
    const colons = bare.split(':').length - 1;
    if (colons >= 2 && /^[0-9a-fA-F:.]+$/.test(bare))
        return true;
    return false;
}
function asCodeLike(value) {
    if (typeof value !== 'string')
        return undefined;
    return CODE_RE.test(value) ? value : undefined;
}
/** Guard {@link CodeStr} (§2 `CODE`). */
export function isCodeStr(value) {
    return asCodeLike(value) !== undefined;
}
/** Konstruktor {@link CodeStr}. Mengembalikan `undefined` bila ditolak — **tak pernah melempar**. */
export function codeStr(value) {
    return asCodeLike(value);
}
/** Guard {@link NonEmptyStr} — kosong / hanya spasi ditolak (bukti `log_reprint` VTEC: **18% kosong**, §10). */
export function isNonEmptyStr(value) {
    return typeof value === 'string' && value.trim().length > 0 && value.length <= TEXT_MAX;
}
/**
 * Konstruktor {@link NonEmptyStr}.
 *
 * ⚠️ **Tidak men-trim.** Parser dilarang mengubah nilai: round-trip kawat harus lossless
 * (`parseEvent(JSON.parse(JSON.stringify(e))) === e`). Ia hanya **menilai**.
 */
export function nonEmptyStr(value) {
    return isNonEmptyStr(value) ? value : undefined;
}
function asUuidV7(value) {
    if (typeof value !== 'string')
        return undefined;
    return UUID_V7_RE.test(value) ? value : undefined;
}
/** Guard UUIDv7 (§3, SYNC §1: v4 merusak lokalitas indeks — **v7 wajib**). */
export function isUuidV7(value) {
    return asUuidV7(value) !== undefined;
}
/** Konstruktor {@link EventId} (UUIDv7). Nilai ini **kunci dedupe** — lihat {@link EventEnvelope}. */
export function eventId(value) {
    return asUuidV7(value);
}
/** Konstruktor {@link OrderId} (UUIDv7 §3). */
export function orderId(value) {
    return asUuidV7(value);
}
/** Konstruktor {@link ShiftId} (UUIDv7 §3). */
export function shiftId(value) {
    return asUuidV7(value);
}
/** Konstruktor {@link MovementId} (UUIDv7 §3, §9). */
export function movementId(value) {
    return asUuidV7(value);
}
/** Konstruktor {@link RefundId} (UUIDv7 §3, §10). */
export function refundId(value) {
    return asUuidV7(value);
}
/**
 * ⭐ Konstruktor {@link OrderNo} — **satu-satunya** jalan membentuknya (§10b lapis ①).
 * Memvalidasi `<outlet_code>/<yymmdd>/<urut>` (§3) ⇒ UUID ditolak di runtime, dan di tingkat tipe
 * ia bahkan tak bisa di-assign tanpa lewat sini.
 */
export function orderNo(value) {
    if (typeof value !== 'string')
        return undefined;
    return ORDER_NO_RE.test(value) ? value : undefined;
}
/** Konstruktor {@link ShiftNo} (kunci natural, §2 `CODE`). */
export function shiftNo(value) {
    return asCodeLike(value);
}
/** Konstruktor {@link TenantCode} (K1). */
export function tenantCode(value) {
    return asCodeLike(value);
}
/** Konstruktor {@link OutletCode} — kunci natural wajib di amplop (§10b). */
export function outletCode(value) {
    return asCodeLike(value);
}
/** Konstruktor {@link StaffCode} (§4b — snapshot, bukan FK). */
export function staffCode(value) {
    return asCodeLike(value);
}
/** Konstruktor {@link MemberCode} (R3 §10b — kunci natural milik Loyalty). */
export function memberCode(value) {
    return asCodeLike(value);
}
/** Konstruktor {@link ItemCode} (§9). */
export function itemCode(value) {
    return asCodeLike(value);
}
/** Konstruktor {@link ServiceCode}. Dicadangkan untuk pemancar sisi server. */
export function serviceCode(value) {
    return asCodeLike(value);
}
/**
 * ⭐ Konstruktor {@link DeviceCode} — **menolak string yang berbentuk alamat IP** (P10).
 * Lihat {@link looksLikeIpAddress} untuk buktinya.
 */
export function deviceCode(value) {
    if (typeof value !== 'string')
        return undefined;
    if (looksLikeIpAddress(value))
        return undefined;
    return asCodeLike(value);
}
/** Guard {@link Timestamp} — ISO-8601 **UTC `Z`** dan tanggal kalender yang benar-benar ada (§2). */
export function isTimestamp(value) {
    if (typeof value !== 'string')
        return false;
    const m = TS_RE.exec(value);
    if (m === null)
        return false;
    const [, y, mo, d, hh, mm, ss] = m;
    if (y === undefined || mo === undefined || d === undefined)
        return false;
    if (hh === undefined || mm === undefined || ss === undefined)
        return false;
    if (!isValidYmd(Number(y), Number(mo), Number(d)))
        return false;
    // Detik kabisat (`:60`) sengaja ditolak: `TS` disimpan sebagai instan UTC (§2), bukan UTC-SLS.
    return Number(hh) <= 23 && Number(mm) <= 59 && Number(ss) <= 59;
}
/**
 * Konstruktor {@link Timestamp}. `"2026-08-01T10:00:00+07:00"` ditolak: §2 mensyaratkan
 * **selalu UTC di penyimpanan**, dan offset lokal di kawat berarti dua lapis bisa menafsir beda.
 */
export function utcTimestamp(value) {
    return isTimestamp(value) ? value : undefined;
}
/** Guard {@link BusinessDate} — `YYYY-MM-DD` + kalender nyata (§2 `BDATE`). */
export function isBusinessDate(value) {
    if (typeof value !== 'string')
        return false;
    const m = BDATE_RE.exec(value);
    if (m === null)
        return false;
    const [, y, mo, d] = m;
    if (y === undefined || mo === undefined || d === undefined)
        return false;
    return isValidYmd(Number(y), Number(mo), Number(d));
}
/** Konstruktor {@link BusinessDate} (§2). `"01/08/2026"` dan `"2026-8-1"` ditolak. */
export function businessDate(value) {
    return isBusinessDate(value) ? value : undefined;
}
/* ────────────────────────────────────────────────────────────────────────────
 * 3. Daftar nama peristiwa (§10b)
 * ──────────────────────────────────────────────────────────────────────────── */
/** Bentuk sah sebuah nama peristiwa: `<agregat>.<lampau>` — dipakai juga di uji regresi. */
export const EVENT_NAME_PATTERN = /^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/;
/**
 * ⭐ Daftar peristiwa **v0**, unik dan terurut menaik. `Object.freeze` — bukan hiasan: nama ini
 * tersimpan di `webhook_endpoint.events[]` (§10b), jadi menambalnya saat runtime = mengubah
 * kontrak sebuah tenant tanpa jejak.
 *
 * | Nama | Dipancarkan kapan | Konsumen |
 * |---|---|---|
 * | `business_day.closed` | hari usaha ditutup (Z-report), per outlet | **ERP** · portal |
 * | `customer.identified` | tamu dikenali di bill (scan member / pilih dari daftar) | Loyalty · Reservasi |
 * | `order.opened` | baris `order` pertama kali dibuat | Reservasi · portal · BI |
 * | `order.paid` | Σ tender = `grand_total` dan pesanan tertutup | **ERP** · Loyalty · Reservasi · BI |
 * | `order.refunded` | pengembalian atas pesanan yang **sudah** `paid` | ERP · Loyalty · BI |
 * | `order.sent` | satu `send_round` dikirim ke dapur (§6) | BI · portal |
 * | `order.voided` | pesanan dibatalkan seluruhnya | ERP · portal · BI |
 * | `shift.closed` | sesi kas ditutup + selisih dicatat | ERP · portal · HR |
 * | `shift.opened` | sesi kas dibuka | portal (kontrol) · HR |
 * | `stock.moved` | baris `stock_movement` ditulis (append-only §9) | BI · portal · ERP |
 *
 * ### Penyimpangan dari "kontrak awal" §10b, dan alasannya
 *
 * **➕ `shift.opened` — TAMBAH.** `shift.closed` sendirian tak bisa mendeteksi shift yang **dibuka
 * lalu tak pernah ditutup**: tak ada peristiwa yang dipancarkan sama sekali. Itu persis pola gagal
 * yang dilarang DESIGN §5b — *"kontrol gagal bukan karena salah hitung, melainkan karena berhenti
 * dijalankan tanpa memberi sinyal apa pun"* (bukti: VTEC **43 hari lolos tanpa tutup**). Alarm
 * berbasis KONDISI (R2 §10b) butuh titik awal; inilah titik awal itu.
 *
 * **➕ `business_day.closed` — TAMBAH.** Yang ERP terima adalah **ringkasan harian per outlet**
 * (SYNC §4; `arenanetsuite` §4b: `sales` = 1 baris/outlet/hari, terbukti 2,7 tahun). Tanpa
 * peristiwa "hari ini ditutup", portal tak punya pemicu sah untuk menyusunnya — ia terpaksa
 * menebak lewat jam dinding, padahal `business_date` ditentukan `outlet.day_cutover_time` dan
 * **bukan** turunan UTC (§2). Di sinilah kontrol memaksa DESIGN §5b hidup.
 *
 * **✅ `order.sent` — TETAP, meski KDS memakai push (K5).** Data KDS **fana, retensi 7–30 hari**
 * (P9 §10), sedangkan kalibrasi ambang SLA menuntut data nyata jangka panjang (DESIGN §5b: *ambang
 * dikalibrasi dari data nyata* — bukti: **39% item VTEC melewati ambang kritis**). Dan `sent_at`
 * adalah **batas rezim kontrol**: void sebelum kirim = normal, sesudah kirim = `priority_review`
 * (§10). Batas itu wajib awet lebih lama dari KDS.
 *
 * **❌ `member.points_earned` — DIHAPUS** (lihat {@link RESERVED_EVENT_NAMES}).
 */
export const EVENT_NAMES = Object.freeze([
    'business_day.closed',
    'customer.identified',
    'order.opened',
    'order.paid',
    'order.refunded',
    'order.sent',
    'order.voided',
    'shift.closed',
    'shift.opened',
    'stock.moved',
]);
/**
 * Nama yang **dicadangkan selamanya** tapi **tidak** dipancarkan v0. Ada di sini karena nama
 * bersifat selamanya: mencadangkannya sekarang berbiaya nol, memakainya ulang untuk makna lain
 * kelak berbiaya senyap.
 *
 * | Nama | Kenapa belum / tidak |
 * |---|---|
 * | `order.ready` · `order.served` | konsumennya (agregator delivery) baru **usulan** §10b ② dan belum ada. Memancarkan aliran tanpa pelanggan = pola R2 yang kita hukum sendiri |
 * | `order.line_voided` | audit per baris hidup di `order_line_event` (§10, DESIGN §2) + record sync; menaikkannya ke outbox mengundang ledakan 26× (SYNC §3) |
 * | `attendance.recorded` · `attendance.correction_approved` | POS memang pemiliknya (§4b ③) dan wajib mendorong ke HR — tapi **companion HR belum ada** (§12) |
 * | `member.points_awarded` | arah **masuk** (Loyalty → POS), bukan keluar |
 * | `member.points_earned` | ⛔ **DIHAPUS dari kontrak awal §10b.** Melanggar **R3**: pemilik saldo poin adalah Loyalty; POS boleh *"membaca salinan; mengirim peristiwa"* — tapi *"poin bertambah 120"* adalah **hasil perhitungan** milik Loyalty, bukan pengamatan POS ⇒ *"dua sistem sama-sama merasa berhak mengubah satu angka"*. Juga melanggar **K12/§12** (mesin poin ditunda; bukti: poin Arena hidup di Monggo, tabel internal VTEC kosong). Penggantinya sudah ada tanpa tambahan apa pun: `order.paid` membawa `customer.member_code` + gross/diskon/pajak ⇒ Loyalty menghitung poinnya sendiri |
 */
export const RESERVED_EVENT_NAMES = Object.freeze([
    'attendance.correction_approved',
    'attendance.recorded',
    'member.points_awarded',
    'member.points_earned',
    'order.line_voided',
    'order.ready',
    'order.served',
]);
const EVENT_NAME_SET = new Set(EVENT_NAMES);
/**
 * Guard {@link EventName} — **case-sensitive dan tanpa toleransi**: `'ORDER.PAID'`,
 * `'order.paid.v2'`, `'orderpaid'`, `'order.completed'` semuanya ditolak (C3/P2 — tak ada fallback).
 */
export function isEventName(value) {
    return typeof value === 'string' && EVENT_NAME_SET.has(value);
}
/**
 * Versi payload **per nama**, bukan satu angka global — bentuk `order.paid` bisa berubah tanpa
 * memaksa seluruh konsumen `stock.moved` ikut naik versi.
 *
 * Aturan bump: naik **hanya** bila field dihapus atau berubah tipe. **Penambahan field = versi
 * tetap** (konsumen lama mengabaikan yang tak dikenalnya di sisi mereka; parser kita tetap
 * fail-closed terhadap field asing pada versi yang dikenalnya).
 */
export const EVENTS_SCHEMA_VERSION = Object.freeze({
    'business_day.closed': 1,
    'customer.identified': 1,
    'order.opened': 1,
    // v2 — `tax_exempt_reason` DIHAPUS: pengecualian pajak melekat pada BARIS pesanan (§8/K11),
    //      bukan pada bill. Lihat OrderPaidPayload.taxes.
    // v3 — `taxes[].is_inclusive_snapshot` DITAMBAH dan **wajib**. Naik versi (bukan tetap seperti
    //      aturan "penambahan field") karena field ini WAJIB: produsen v2 yang tak mengirimkannya
    //      ditolak parser — itu perubahan yang memutus, dan menyamarkannya sebagai penambahan biasa
    //      justru bentuk kegagalan senyap yang R2 larang.
    'order.paid': 3,
    'order.refunded': 1,
    'order.sent': 1,
    'order.voided': 1,
    'shift.closed': 1,
    'shift.opened': 1,
    'stock.moved': 1,
});
/* ────────────────────────────────────────────────────────────────────────────
 * 4. Enum payload (§4 · §5 · §6 · §9) — semuanya union literal + array beku (C1/C2)
 * ──────────────────────────────────────────────────────────────────────────── */
/** §6 `order_type` — **bagaimana disajikan**. */
export const ORDER_TYPES = Object.freeze(['dine_in', 'takeaway', 'delivery']);
/** Guard {@link OrderType}. */
export function isOrderType(v) {
    return typeof v === 'string' && ORDER_TYPES.includes(v);
}
/** §6 `entry_channel` — **dari mana MASUK** (provenance, bukan tipe pesanan). */
export const ENTRY_CHANNELS = Object.freeze([
    'pos',
    'kiosk',
    'waiter_app',
    'web',
    'qr_table',
    'call_center',
    'marketplace',
]);
/** Guard {@link EntryChannel}. */
export function isEntryChannel(v) {
    return typeof v === 'string' && ENTRY_CHANNELS.includes(v);
}
/**
 * §5 `product_price.channel` — **harga MANA yang berlaku**.
 *
 * ⚠️ Lebih halus daripada `PriceChannel` `osbhqpos` yang hanya 3 nilai: GoFood, GrabFood, dan
 * ShopeeFood punya komisi berbeda ⇒ butuh harga & akun kliring terpisah (§5, §7).
 */
export const PRICE_CHANNELS = Object.freeze([
    'dine_in',
    'takeaway',
    'delivery_gofood',
    'delivery_grab',
    'delivery_shopee',
    'kiosk',
]);
/** Guard {@link PriceChannel}. */
export function isPriceChannel(v) {
    return typeof v === 'string' && PRICE_CHANNELS.includes(v);
}
/**
 * §4 `table_kind` — ⭐ **meja semu jadi EKSPLISIT**. Di VTEC, quick-service memakai meja bernomor
 * ajaib `'100'`; angka ajaib dilarang ⇒ enum. Konsekuensinya QSR dan fine dining berjalan di
 * **satu mekanisme**, bukan dua jalur kode.
 */
export const TABLE_KINDS = Object.freeze([
    'physical',
    'virtual_quick',
    'virtual_takeaway',
    'virtual_delivery',
    'virtual_kiosk',
]);
/** Guard {@link TableKind}. */
export function isTableKind(v) {
    return typeof v === 'string' && TABLE_KINDS.includes(v);
}
/**
 * §9 `stock_movement.movement_type` — **jenisnya menyatakan sifatnya**.
 * `sale_backflush` = TEORETIS (proyeksi); sisanya AKTUAL, dan `opname_adjustment` yang mengoreksi.
 */
export const MOVEMENT_TYPES = Object.freeze([
    'receipt',
    'sale_backflush',
    'waste',
    'transfer_in',
    'transfer_out',
    'opname_adjustment',
]);
/** Guard {@link MovementType}. */
export function isMovementType(v) {
    return typeof v === 'string' && MOVEMENT_TYPES.includes(v);
}
/**
 * Peta `movement_type → is_theoretical` (§9). **Beku dan tak dapat dikonfigurasi tenant**: sifat
 * teoretis-vs-aktual adalah taksonomi, bukan setelan. Selisih teoretis-vs-aktual **adalah
 * produknya**, bukan kesalahan yang disembunyikan.
 */
export const MOVEMENT_IS_THEORETICAL = Object.freeze({
    receipt: false,
    sale_backflush: true,
    waste: false,
    transfer_in: false,
    transfer_out: false,
    opname_adjustment: false,
});
/** Cara tamu dikenali di bill (§10b — POS mengirim peristiwa, Loyalty yang memiliki saldonya). */
export const IDENTIFIED_VIA = Object.freeze(['scan', 'manual_select', 'phone']);
/** Guard {@link IdentifiedVia}. */
export function isIdentifiedVia(v) {
    return typeof v === 'string' && IDENTIFIED_VIA.includes(v);
}
/* ────────────────────────────────────────────────────────────────────────────
 * 5. `ReasonCode` — enum + catatan opsional (§10)
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Alasan terstruktur. **Bukti kenapa enum, bukan teks bebas (§10):** `log_reprint` VTEC beralasan
 * teks bebas ⇒ **18% kosong**, sisanya `CUSTOMER REQUEST` / `copy` / `C` / `v` / `duplikat` —
 * *kontrolnya benar, datanya tak bisa dianalisis*.
 *
 * ⚠️ Daftar **nilainya** adalah keputusan kita; yang berasal dari bukti VTEC adalah
 * **mekanismenya** (enum wajib + `reason_note` opsional), bukan tiap katanya.
 *
 * Catatan asal beberapa nilai:
 * - `item_unavailable` — "86", ketersediaan lokal outlet (SYNC §2: countdown stok lokal, tak disinkron).
 * - `staff_offline_substitute` — §4b: saat luring, shift berjalan di bawah identitas supervisor.
 * - `cash_variance` — DESIGN §5b: selisih kas wajib beralasan (bukti: `Remark` terisi **17 dari 8.125 hari**).
 */
export const REASON_CODES = Object.freeze([
    /** Setor sebagian isi laci ke brankas di tengah sesi. */
    'cash_drop',
    'cash_variance',
    'customer_cancelled',
    'customer_complaint',
    'customer_request',
    /**
     * ⭐ **Perangkat pemegang kunci sunting MATI** — alasan paksa-buka yang sebenarnya.
     *
     * ⛔ Dipisahkan dari `system_error` dengan sengaja: "berapa sering perangkat kita mati di tengah
     * pelayanan?" adalah pertanyaan yang membenarkan (atau membatalkan) belanja tablet baru, dan ia
     * mustahil dijawab kalau kejadiannya bercampur dengan galat sistem lain.
     */
    'device_down',
    'duplicate_entry',
    'item_unavailable',
    'kitchen_error',
    /** Buka laci untuk menukar uang tamu (tak ada penjualan). */
    'make_change',
    /** ⭐ Kasir lupa menerapkan diskon sebelum menutup bill. */
    'missed_discount',
    'other',
    /**
     * ⭐ Ambil/simpan **barang pribadi** di laci (kunci, ponsel) — kejadian sehari-hari, bukan
     * peristiwa uang. Dibedakan justru supaya ia **tidak** ikut menaikkan alarm: kalau buka laci
     * untuk mengambil kunci ditandai "wajib ditinjau", daftarnya merah semua lalu tak pernah dibaca.
     */
    'personal_item',
    'printer_failure',
    'reprint_copy',
    'staff_offline_substitute',
    'stock_opname',
    'stock_transfer',
    'stock_waste',
    'system_error',
    'test_transaction',
    'training',
    'wrong_item',
    'wrong_price',
    'wrong_qty',
    /** ⭐ Kasir salah memilih metode bayar — tunai dicatat QRIS, atau sebaliknya. */
    'wrong_tender',
]);
/** Guard {@link ReasonCode} — `'C'`, `'copy'`, `''` ditolak (regresi langsung atas `log_reprint`). */
export function isReasonCode(v) {
    return typeof v === 'string' && REASON_CODES.includes(v);
}
/**
 * Alasan sah untuk `order.voided` (§10). **Subset — P2:** alasan cetak-ulang (`reprint_copy`) dan
 * alasan stok tak boleh muncul di sini, dan salah-kamar = **gagal kompilasi**, bukan gagal review.
 */
export const VOID_REASONS = Object.freeze([
    'customer_cancelled',
    'customer_request',
    'duplicate_entry',
    'item_unavailable',
    'kitchen_error',
    /** Buka laci untuk menukar uang tamu (tak ada penjualan). */
    'make_change',
    /** ⭐ Kasir lupa menerapkan diskon sebelum menutup bill. */
    'missed_discount',
    'other',
    /**
     * ⭐ Ambil/simpan **barang pribadi** di laci (kunci, ponsel) — kejadian sehari-hari, bukan
     * peristiwa uang. Dibedakan justru supaya ia **tidak** ikut menaikkan alarm: kalau buka laci
     * untuk mengambil kunci ditandai "wajib ditinjau", daftarnya merah semua lalu tak pernah dibaca.
     */
    'personal_item',
    'system_error',
    'test_transaction',
    'training',
    'wrong_item',
    'wrong_price',
    'wrong_qty',
    /** ⭐ Kasir salah memilih metode bayar — tunai dicatat QRIS, atau sebaliknya. */
    'wrong_tender',
]);
/** Guard {@link VoidReason}. */
export function isVoidReason(v) {
    return typeof v === 'string' && VOID_REASONS.includes(v);
}
/** Alasan sah untuk `order.refunded` (§10). Subset — P2. */
export const REFUND_REASONS = Object.freeze([
    'customer_complaint',
    'customer_request',
    'duplicate_entry',
    'kitchen_error',
    /** Buka laci untuk menukar uang tamu (tak ada penjualan). */
    'make_change',
    /** ⭐ Kasir lupa menerapkan diskon sebelum menutup bill. */
    'missed_discount',
    'other',
    /**
     * ⭐ Ambil/simpan **barang pribadi** di laci (kunci, ponsel) — kejadian sehari-hari, bukan
     * peristiwa uang. Dibedakan justru supaya ia **tidak** ikut menaikkan alarm: kalau buka laci
     * untuk mengambil kunci ditandai "wajib ditinjau", daftarnya merah semua lalu tak pernah dibaca.
     */
    'personal_item',
    'system_error',
    'wrong_item',
    'wrong_price',
    'wrong_qty',
    /** ⭐ Kasir salah memilih metode bayar — tunai dicatat QRIS, atau sebaliknya. */
    'wrong_tender',
]);
/** Guard {@link RefundReason}. */
export function isRefundReason(v) {
    return typeof v === 'string' && REFUND_REASONS.includes(v);
}
/** Alasan sah untuk selisih kas di `shift.closed` (DESIGN §5b). Subset — P2. */
export const CASH_VARIANCE_REASONS = Object.freeze([
    /** Setor sebagian isi laci ke brankas di tengah sesi. */
    'cash_drop',
    'cash_variance',
    'other',
    /**
     * ⭐ Ambil/simpan **barang pribadi** di laci (kunci, ponsel) — kejadian sehari-hari, bukan
     * peristiwa uang. Dibedakan justru supaya ia **tidak** ikut menaikkan alarm: kalau buka laci
     * untuk mengambil kunci ditandai "wajib ditinjau", daftarnya merah semua lalu tak pernah dibaca.
     */
    'personal_item',
    'staff_offline_substitute',
    'system_error',
    'test_transaction',
    'training',
]);
/** Guard {@link CashVarianceReason}. */
export function isCashVarianceReason(v) {
    return typeof v === 'string' && CASH_VARIANCE_REASONS.includes(v);
}
/**
 * Alasan sah untuk `stock.moved` (§9). Subset — P2.
 *
 * ⚠️ Daftar ini sengaja **tidak** memuat nilai untuk `receipt` maupun `sale_backflush`: alasan
 * kedua jenis itu **struktural**, bukan pilihan manusia (dokumen penerimaan / resep + pesanan,
 * yang sudah dibawa `source_ref`). Memaksa keduanya beralasan akan menjadikan `other` nilai
 * mayoritas — persis penyakit teks-bebas yang enum ini ada untuk menyembuhkan (§10 `log_reprint`).
 * ⇒ lihat aturan `reason`/`source_ref` di {@link StockMovedPayload}.
 */
export const STOCK_REASONS = Object.freeze([
    'item_unavailable',
    'other',
    /**
     * ⭐ Ambil/simpan **barang pribadi** di laci (kunci, ponsel) — kejadian sehari-hari, bukan
     * peristiwa uang. Dibedakan justru supaya ia **tidak** ikut menaikkan alarm: kalau buka laci
     * untuk mengambil kunci ditandai "wajib ditinjau", daftarnya merah semua lalu tak pernah dibaca.
     */
    'personal_item',
    'stock_opname',
    'stock_transfer',
    'stock_waste',
    'system_error',
]);
/** Guard {@link StockReason}. */
export function isStockReason(v) {
    return typeof v === 'string' && STOCK_REASONS.includes(v);
}
/** `emitted_by.kind` yang sah. */
export const EMITTED_BY_KINDS = Object.freeze(['device', 'server']);
/** `actor_kind` yang sah (§10). */
export const ACTOR_KINDS = Object.freeze(['staff', 'customer', 'system', 'agent']);
/** `source_ref.kind` yang sah (§9). */
export const STOCK_SOURCE_KINDS = Object.freeze(['recipe', 'order', 'document']);
/* ────────────────────────────────────────────────────────────────────────────
 * 11. Hasil parse — kode, bukan pesan bebas (R2)
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Kode kesalahan parse — **stabil dan mesin-baca**. Bukan pesan bebas: R2 §10b menuntut kegagalan
 * integrasi **terlihat**, dan yang bisa dipantau/di-alarm-kan adalah kode, bukan kalimat.
 * (Penyakit yang sama dengan alasan teks-bebas `log_reprint` — §10.)
 */
export const PARSE_ERROR_CODES = Object.freeze([
    'array_empty',
    'backflush_source_required',
    'business_date_invalid',
    'card_last4_invalid',
    'code_invalid',
    'device_code_looks_like_ip',
    'edc_approval_required',
    'edc_mode_unknown',
    'enum_unknown',
    'event_name_unknown',
    'grand_total_mismatch',
    'member_id_forbidden',
    'missing_field',
    'money_invalid',
    'nonempty_required',
    'not_array',
    'not_boolean',
    'not_integer',
    'not_object',
    'order_no_invalid',
    'priority_review_required',
    'qty_invalid',
    'qty_zero',
    'rate_invalid',
    'reason_code_unknown',
    'reason_note_required',
    'schema_too_new',
    'schema_version_invalid',
    'service_charge_total_mismatch',
    'supervisor_approval_required',
    'tax_total_mismatch',
    'tender_sum_mismatch',
    'tender_type_unknown',
    'theoretical_flag_mismatch',
    'timestamp_invalid',
    'unknown_field',
    'uuid_v7_invalid',
    'variance_mismatch',
    'variance_reason_required',
]);
/** Penanda "field tak ada" — dibedakan dari `undefined` agar kesalahan tak dilaporkan dua kali. */
const MISSING = Symbol('missing');
function bad(e, path, code) {
    e.push(Object.freeze({ path, code }));
    return undefined;
}
function asRecord(v, path, e) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return bad(e, path, 'not_object');
    return v;
}
/**
 * **Fail-closed (P6):** field tak dikenal ⇒ **ditolak**, bukan diabaikan. Field asing di amplop
 * (mis. `ip_address`) adalah tanda pengirim memakai kontrak lain — memaafkannya berarti perbedaan
 * kontrak berjalan diam-diam sampai ada yang menghitung selisih.
 */
function noExtraKeys(obj, allowed, path, e) {
    const set = new Set(allowed);
    for (const key of Object.keys(obj)) {
        if (!set.has(key))
            bad(e, `${path}.${key}`, 'unknown_field');
    }
}
function has(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
}
function pick(obj, key, path, e) {
    if (!has(obj, key)) {
        bad(e, `${path}.${key}`, 'missing_field');
        return MISSING;
    }
    return obj[key];
}
function field(obj, key, path, e, ctor, code) {
    const raw = pick(obj, key, path, e);
    if (raw === MISSING)
        return undefined;
    const v = ctor(raw);
    if (v === undefined)
        return bad(e, `${path}.${key}`, code);
    return v;
}
/** Varian nullable: **`X | null` di kawat, bukan properti opsional** — round-trip JSON lossless. */
function fieldOrNull(obj, key, path, e, ctor, code) {
    const raw = pick(obj, key, path, e);
    if (raw === MISSING)
        return undefined;
    if (raw === null)
        return null;
    const v = ctor(raw);
    if (v === undefined)
        return bad(e, `${path}.${key}`, code);
    return v;
}
/** P1 — `number` **tak pernah** diterima untuk uang; hanya string desimal kanonik skala 4. */
const asMoney = (v) => (isMoneyString(v) ? v : undefined);
/** P1 — kuantitas: string desimal kanonik skala 6 (§2 `QTY` desimal, bukan integer). */
const asQty = (v) => (isQtyString(v) ? v : undefined);
/** P1 — tarif: string desimal kanonik skala 6, **pecahan** (§2: `"0.100000"` = 10%). */
const asRate = (v) => (isRateString(v) ? v : undefined);
function asPlainString(max) {
    return (v) => typeof v === 'string' && v.trim().length > 0 && v.length <= max ? v : undefined;
}
function asApprovalCode(v) {
    return typeof v === 'string' && APPROVAL_CODE_RE.test(v) ? v : undefined;
}
function asCardLast4(v) {
    return isCardLast4(v) ? v : undefined;
}
/** `edc_terminal_id` = identitas mesin EDC — P10 berlaku sama seperti `device_code`. */
function asEdcTerminalId(v) {
    if (typeof v !== 'string' || looksLikeIpAddress(v))
        return undefined;
    return codeStr(v);
}
function intField(obj, key, path, e, min) {
    const raw = pick(obj, key, path, e);
    if (raw === MISSING)
        return undefined;
    if (typeof raw !== 'number' || !Number.isSafeInteger(raw) || raw < min) {
        return bad(e, `${path}.${key}`, 'not_integer');
    }
    return raw;
}
function boolField(obj, key, path, e) {
    const raw = pick(obj, key, path, e);
    if (raw === MISSING)
        return undefined;
    if (typeof raw !== 'boolean')
        return bad(e, `${path}.${key}`, 'not_boolean');
    return raw;
}
function enumField(obj, key, path, e, guard, code = 'enum_unknown') {
    const raw = pick(obj, key, path, e);
    if (raw === MISSING)
        return undefined;
    if (!guard(raw))
        return bad(e, `${path}.${key}`, code);
    return raw;
}
function arrayField(obj, key, path, e, minLength) {
    const raw = pick(obj, key, path, e);
    if (raw === MISSING)
        return undefined;
    if (!Array.isArray(raw))
        return bad(e, `${path}.${key}`, 'not_array');
    if (raw.length < minLength)
        return bad(e, `${path}.${key}`, 'array_empty');
    return raw;
}
/* ── Rujukan berkunci natural (§10b lapis ④: guard runtime, karena konsumen luar tak
 *    mengompilasi TypeScript kita — tipe saja tak cukup). ─────────────────────────── */
function parseOrderRef(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    noExtraKeys(obj, ['order_id', 'order_no'], path, e);
    const id = field(obj, 'order_id', path, e, orderId, 'uuid_v7_invalid');
    const no = field(obj, 'order_no', path, e, orderNo, 'order_no_invalid');
    if (id === undefined || no === undefined)
        return undefined;
    return Object.freeze({ order_id: id, order_no: no });
}
function parseMemberRef(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    // ⛔ R3 §10b: UUID member milik Loyalty. Menyimpannya di POS = pintu belakang menuju
    //    "POS ikut memiliki saldo poin". Ditolak eksplisit, bukan sekadar diabaikan.
    if (has(obj, 'member_id'))
        bad(e, `${path}.member_id`, 'member_id_forbidden');
    noExtraKeys(obj, ['member_code', 'member_id'], path, e);
    const code = field(obj, 'member_code', path, e, memberCode, 'code_invalid');
    if (code === undefined || has(obj, 'member_id'))
        return undefined;
    return Object.freeze({ member_code: code });
}
function parseShiftRef(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    noExtraKeys(obj, ['shift_id', 'shift_no'], path, e);
    const id = field(obj, 'shift_id', path, e, shiftId, 'uuid_v7_invalid');
    const no = field(obj, 'shift_no', path, e, shiftNo, 'code_invalid');
    if (id === undefined || no === undefined)
        return undefined;
    return Object.freeze({ shift_id: id, shift_no: no });
}
function parseStockRef(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    noExtraKeys(obj, ['movement_id', 'item_code'], path, e);
    const id = field(obj, 'movement_id', path, e, movementId, 'uuid_v7_invalid');
    const code = field(obj, 'item_code', path, e, itemCode, 'code_invalid');
    if (id === undefined || code === undefined)
        return undefined;
    return Object.freeze({ movement_id: id, item_code: code });
}
function parseDayRef(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    noExtraKeys(obj, ['business_date'], path, e);
    const d = field(obj, 'business_date', path, e, businessDate, 'business_date_invalid');
    if (d === undefined)
        return undefined;
    return Object.freeze({ business_date: d });
}
/* ── Aktor & pemancar ───────────────────────────────────────────────────────── */
function parseActor(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    const kind = obj['actor_kind'];
    switch (kind) {
        case 'staff': {
            noExtraKeys(obj, ['actor_kind', 'staff_code_snapshot', 'staff_name_snapshot'], path, e);
            const code = field(obj, 'staff_code_snapshot', path, e, staffCode, 'code_invalid');
            const name = field(obj, 'staff_name_snapshot', path, e, nonEmptyStr, 'nonempty_required');
            if (code === undefined || name === undefined)
                return undefined;
            return Object.freeze({
                actor_kind: 'staff',
                staff_code_snapshot: code,
                staff_name_snapshot: name,
            });
        }
        case 'customer': {
            noExtraKeys(obj, ['actor_kind', 'member'], path, e);
            const raw = pick(obj, 'member', path, e);
            if (raw === MISSING)
                return undefined;
            // `null` = kiosk anonim — SAH (DESIGN §4①: pelaku bisa BUKAN staf, dan bisa tak dikenal).
            if (raw === null)
                return Object.freeze({ actor_kind: 'customer', member: null });
            const member = parseMemberRef(raw, `${path}.member`, e);
            if (member === undefined)
                return undefined;
            return Object.freeze({ actor_kind: 'customer', member });
        }
        case 'system': {
            noExtraKeys(obj, ['actor_kind', 'process_code'], path, e);
            const code = field(obj, 'process_code', path, e, codeStr, 'code_invalid');
            if (code === undefined)
                return undefined;
            return Object.freeze({ actor_kind: 'system', process_code: code });
        }
        case 'agent': {
            noExtraKeys(obj, ['actor_kind', 'agent_code'], path, e);
            const code = field(obj, 'agent_code', path, e, codeStr, 'code_invalid');
            if (code === undefined)
                return undefined;
            return Object.freeze({ actor_kind: 'agent', agent_code: code });
        }
        default:
            return bad(e, `${path}.actor_kind`, 'enum_unknown');
    }
}
function parseActorOrNull(v, path, e) {
    if (v === null)
        return null;
    return parseActor(v, path, e);
}
function parseEmittedBy(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    const kind = obj['kind'];
    if (kind === 'device') {
        noExtraKeys(obj, ['kind', 'device_code'], path, e);
        const raw = pick(obj, 'device_code', path, e);
        if (raw === MISSING)
            return undefined;
        // P10 — kontrol yang MEMAKSA: alamat IP ditolak dengan kodenya sendiri agar terlihat di alarm.
        if (typeof raw === 'string' && looksLikeIpAddress(raw)) {
            return bad(e, `${path}.device_code`, 'device_code_looks_like_ip');
        }
        const code = deviceCode(raw);
        if (code === undefined)
            return bad(e, `${path}.device_code`, 'code_invalid');
        return Object.freeze({ kind: 'device', device_code: code });
    }
    if (kind === 'server') {
        noExtraKeys(obj, ['kind', 'service_code'], path, e);
        const code = field(obj, 'service_code', path, e, serviceCode, 'code_invalid');
        if (code === undefined)
            return undefined;
        return Object.freeze({ kind: 'server', service_code: code });
    }
    return bad(e, `${path}.kind`, 'enum_unknown');
}
/* ── Alasan (§10) ───────────────────────────────────────────────────────────── */
/**
 * Parse `Reasoned<C>` terhadap **subset** yang sah untuk peristiwa itu (P2). Bentuk runtime-nya
 * sengaja non-generik; pemanggil yang menyempitkan ke `Reasoned<VoidReason>` dst. sudah terbukti
 * benar karena `allowed` yang dioper berasal dari daftar subset yang sama.
 */
function parseReasoned(v, allowed, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    noExtraKeys(obj, ['reason_code', 'reason_note'], path, e);
    const rawCode = pick(obj, 'reason_code', path, e);
    if (rawCode === MISSING)
        return undefined;
    if (typeof rawCode !== 'string' || !allowed.has(rawCode) || !isReasonCode(rawCode)) {
        return bad(e, `${path}.reason_code`, 'reason_code_unknown');
    }
    let note;
    if (has(obj, 'reason_note')) {
        const parsed = nonEmptyStr(obj['reason_note']);
        if (parsed === undefined)
            return bad(e, `${path}.reason_note`, 'nonempty_required');
        note = parsed;
    }
    // ⭐ `other` tanpa penjelasan = teks-bebas jilid dua. Ditutup di runtime DAN di tipe.
    if (rawCode === 'other' && note === undefined) {
        return bad(e, `${path}.reason_note`, 'reason_note_required');
    }
    return note === undefined
        ? Object.freeze({ reason_code: rawCode })
        : Object.freeze({ reason_code: rawCode, reason_note: note });
}
const VOID_REASON_SET = new Set(VOID_REASONS);
const REFUND_REASON_SET = new Set(REFUND_REASONS);
const CASH_VARIANCE_REASON_SET = new Set(CASH_VARIANCE_REASONS);
const STOCK_REASON_SET = new Set(STOCK_REASONS);
/* ── Bentuk pendukung ───────────────────────────────────────────────────────── */
function parseEdcInfo(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    noExtraKeys(obj, ['edc_mode', 'approval_code', 'card_last4', 'edc_terminal_id', 'batch_no'], path, e);
    const mode = enumField(obj, 'edc_mode', path, e, isEdcMode, 'edc_mode_unknown');
    const approval = fieldOrNull(obj, 'approval_code', path, e, asApprovalCode, 'code_invalid');
    const last4 = fieldOrNull(obj, 'card_last4', path, e, asCardLast4, 'card_last4_invalid');
    const terminal = fieldOrNull(obj, 'edc_terminal_id', path, e, asEdcTerminalId, 'code_invalid');
    const batch = fieldOrNull(obj, 'batch_no', path, e, asPlainString(32), 'code_invalid');
    if (mode === undefined ||
        approval === undefined ||
        last4 === undefined ||
        terminal === undefined ||
        batch === undefined) {
        return undefined;
    }
    // §7/K7 (teardown §8i): `integrated` tanpa approval_code berarti nominal tetap diketik ulang —
    // dua peluang salah, dan itu sumber selisih setoran kasir yang sedang kita hapus.
    if (mode === 'integrated' && approval === null) {
        return bad(e, `${path}.approval_code`, 'edc_approval_required');
    }
    return Object.freeze({
        edc_mode: mode,
        approval_code: approval,
        card_last4: last4,
        edc_terminal_id: terminal,
        batch_no: batch,
    });
}
function parseTenderApplied(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    noExtraKeys(obj, ['payment_method_code', 'tender_type', 'amount', 'reference_no', 'edc'], path, e);
    const code = field(obj, 'payment_method_code', path, e, codeStr, 'code_invalid');
    const type = enumField(obj, 'tender_type', path, e, isTenderType, 'tender_type_unknown');
    const amount = field(obj, 'amount', path, e, asMoney, 'money_invalid');
    const ref = fieldOrNull(obj, 'reference_no', path, e, asPlainString(REFERENCE_MAX), 'code_invalid');
    const rawEdc = pick(obj, 'edc', path, e);
    let edc;
    if (rawEdc === MISSING)
        edc = undefined;
    else if (rawEdc === null)
        edc = null;
    else
        edc = parseEdcInfo(rawEdc, `${path}.edc`, e);
    if (code === undefined ||
        type === undefined ||
        amount === undefined ||
        ref === undefined ||
        edc === undefined) {
        return undefined;
    }
    return Object.freeze({
        payment_method_code: code,
        tender_type: type,
        amount,
        reference_no: ref,
        edc,
    });
}
function parseTenderReversed(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    noExtraKeys(obj, ['payment_method_code', 'tender_type', 'amount'], path, e);
    const code = field(obj, 'payment_method_code', path, e, codeStr, 'code_invalid');
    const type = enumField(obj, 'tender_type', path, e, isTenderType, 'tender_type_unknown');
    const amount = field(obj, 'amount', path, e, asMoney, 'money_invalid');
    if (code === undefined || type === undefined || amount === undefined)
        return undefined;
    return Object.freeze({ payment_method_code: code, tender_type: type, amount });
}
function parseTaxSnapshot(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    noExtraKeys(obj, [
        'tax_code',
        'name_snapshot',
        'kind_snapshot',
        'rate_snapshot',
        'is_inclusive_snapshot',
        'taxable_base',
        'tax_amount',
    ], path, e);
    const code = field(obj, 'tax_code', path, e, codeStr, 'code_invalid');
    const name = field(obj, 'name_snapshot', path, e, nonEmptyStr, 'nonempty_required');
    const kind = enumField(obj, 'kind_snapshot', path, e, isTaxKind, 'enum_unknown');
    const rate = field(obj, 'rate_snapshot', path, e, asRate, 'rate_invalid');
    const inclusive = boolField(obj, 'is_inclusive_snapshot', path, e);
    const base = field(obj, 'taxable_base', path, e, asMoney, 'money_invalid');
    const amount = field(obj, 'tax_amount', path, e, asMoney, 'money_invalid');
    if (code === undefined ||
        name === undefined ||
        kind === undefined ||
        rate === undefined ||
        inclusive === undefined ||
        base === undefined ||
        amount === undefined) {
        return undefined;
    }
    return Object.freeze({
        tax_code: code,
        name_snapshot: name,
        kind_snapshot: kind,
        rate_snapshot: rate,
        is_inclusive_snapshot: inclusive,
        taxable_base: base,
        tax_amount: amount,
    });
}
function parseStockSourceRef(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    const kind = obj['kind'];
    if (kind === 'recipe') {
        noExtraKeys(obj, ['kind', 'recipe_code', 'order_ref'], path, e);
        const code = field(obj, 'recipe_code', path, e, codeStr, 'code_invalid');
        const rawOrder = pick(obj, 'order_ref', path, e);
        if (rawOrder === MISSING || code === undefined)
            return undefined;
        const order = parseOrderRef(rawOrder, `${path}.order_ref`, e);
        if (order === undefined)
            return undefined;
        return Object.freeze({ kind: 'recipe', recipe_code: code, order_ref: order });
    }
    if (kind === 'order') {
        noExtraKeys(obj, ['kind', 'order_ref'], path, e);
        const rawOrder = pick(obj, 'order_ref', path, e);
        if (rawOrder === MISSING)
            return undefined;
        const order = parseOrderRef(rawOrder, `${path}.order_ref`, e);
        if (order === undefined)
            return undefined;
        return Object.freeze({ kind: 'order', order_ref: order });
    }
    if (kind === 'document') {
        noExtraKeys(obj, ['kind', 'document_code'], path, e);
        const code = field(obj, 'document_code', path, e, codeStr, 'code_invalid');
        if (code === undefined)
            return undefined;
        return Object.freeze({ kind: 'document', document_code: code });
    }
    return bad(e, `${path}.kind`, 'enum_unknown');
}
/* ── Aritmetika invarian: EKSAK, lewat `money.ts` (P1) ──────────────────────── */
function toMoney(s) {
    return tryMoney(s);
}
/**
 * Σ nominal, **eksak sebagai bigint** — tak ada epsilon, tak ada float (P1).
 * `sumMoney` bisa melempar `range`; di sini kegagalan itu diterjemahkan jadi `undefined`, karena
 * `parseEvent` **tak pernah melempar**.
 */
function sumStrings(values) {
    const parts = [];
    for (const v of values) {
        const m = toMoney(v);
        if (m === undefined)
            return undefined;
        parts.push(m);
    }
    try {
        return sumMoney(parts);
    }
    catch {
        return undefined;
    }
}
/* ── Payload ────────────────────────────────────────────────────────────────── */
function parseOrderOpened(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    noExtraKeys(obj, ['ref', 'order_type', 'entry_channel', 'price_channel', 'table_code', 'table_kind', 'guest_count'], path, e);
    const rawRef = pick(obj, 'ref', path, e);
    const ref = rawRef === MISSING ? undefined : parseOrderRef(rawRef, `${path}.ref`, e);
    const orderType = enumField(obj, 'order_type', path, e, isOrderType);
    const entry = enumField(obj, 'entry_channel', path, e, isEntryChannel);
    const price = enumField(obj, 'price_channel', path, e, isPriceChannel);
    const tableCode = fieldOrNull(obj, 'table_code', path, e, codeStr, 'code_invalid');
    const tableKind = enumField(obj, 'table_kind', path, e, isTableKind);
    const guests = intField(obj, 'guest_count', path, e, 0);
    if (ref === undefined ||
        orderType === undefined ||
        entry === undefined ||
        price === undefined ||
        tableCode === undefined ||
        tableKind === undefined ||
        guests === undefined) {
        return undefined;
    }
    return Object.freeze({
        ref,
        order_type: orderType,
        entry_channel: entry,
        price_channel: price,
        table_code: tableCode,
        table_kind: tableKind,
        guest_count: guests,
    });
}
function parseOrderSent(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    noExtraKeys(obj, ['ref', 'send_round', 'line_count', 'station_codes'], path, e);
    const rawRef = pick(obj, 'ref', path, e);
    const ref = rawRef === MISSING ? undefined : parseOrderRef(rawRef, `${path}.ref`, e);
    const round = intField(obj, 'send_round', path, e, 1);
    const lines = intField(obj, 'line_count', path, e, 1);
    const rawStations = arrayField(obj, 'station_codes', path, e, 1);
    let stations;
    if (rawStations !== undefined) {
        const out = [];
        let okAll = true;
        for (let i = 0; i < rawStations.length; i += 1) {
            const code = codeStr(rawStations[i]);
            if (code === undefined) {
                bad(e, `${path}.station_codes[${String(i)}]`, 'code_invalid');
                okAll = false;
                continue;
            }
            out.push(code);
        }
        stations = okAll ? out : undefined;
    }
    if (ref === undefined || round === undefined || lines === undefined || stations === undefined) {
        return undefined;
    }
    return Object.freeze({
        ref,
        send_round: round,
        line_count: lines,
        station_codes: Object.freeze(stations),
    });
}
function parseOrderPaid(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    noExtraKeys(obj, [
        'ref',
        'order_type',
        'entry_channel',
        'price_channel',
        'subtotal_gross',
        'discount_total',
        'service_charge_total',
        'tax_total',
        'grand_total',
        'taxes',
        'tenders',
        'customer',
        'closed_by',
    ], path, e);
    const rawRef = pick(obj, 'ref', path, e);
    const ref = rawRef === MISSING ? undefined : parseOrderRef(rawRef, `${path}.ref`, e);
    const orderType = enumField(obj, 'order_type', path, e, isOrderType);
    const entry = enumField(obj, 'entry_channel', path, e, isEntryChannel);
    const price = enumField(obj, 'price_channel', path, e, isPriceChannel);
    // P3 — kelimanya wajib. Payload yang hanya membawa `grand_total` ditolak.
    const subtotal = field(obj, 'subtotal_gross', path, e, asMoney, 'money_invalid');
    const discount = field(obj, 'discount_total', path, e, asMoney, 'money_invalid');
    const service = field(obj, 'service_charge_total', path, e, asMoney, 'money_invalid');
    const taxTotal = field(obj, 'tax_total', path, e, asMoney, 'money_invalid');
    const grand = field(obj, 'grand_total', path, e, asMoney, 'money_invalid');
    const rawTaxes = arrayField(obj, 'taxes', path, e, 0);
    let taxes;
    if (rawTaxes !== undefined) {
        const out = [];
        let okAll = true;
        for (let i = 0; i < rawTaxes.length; i += 1) {
            const row = parseTaxSnapshot(rawTaxes[i], `${path}.taxes[${String(i)}]`, e);
            if (row === undefined)
                okAll = false;
            else
                out.push(row);
        }
        taxes = okAll ? out : undefined;
    }
    const rawTenders = arrayField(obj, 'tenders', path, e, 1);
    let tenders;
    if (rawTenders !== undefined) {
        const out = [];
        let okAll = true;
        for (let i = 0; i < rawTenders.length; i += 1) {
            const row = parseTenderApplied(rawTenders[i], `${path}.tenders[${String(i)}]`, e);
            if (row === undefined)
                okAll = false;
            else
                out.push(row);
        }
        tenders = okAll ? out : undefined;
    }
    const rawCustomer = pick(obj, 'customer', path, e);
    let customer;
    if (rawCustomer === MISSING)
        customer = undefined;
    else if (rawCustomer === null)
        customer = null;
    else
        customer = parseMemberRef(rawCustomer, `${path}.customer`, e);
    const rawClosedBy = pick(obj, 'closed_by', path, e);
    const closedBy = rawClosedBy === MISSING ? undefined : parseActor(rawClosedBy, `${path}.closed_by`, e);
    if (ref === undefined ||
        orderType === undefined ||
        entry === undefined ||
        price === undefined ||
        subtotal === undefined ||
        discount === undefined ||
        service === undefined ||
        taxTotal === undefined ||
        grand === undefined ||
        taxes === undefined ||
        tenders === undefined ||
        customer === undefined ||
        closedBy === undefined) {
        return undefined;
    }
    // ⭐ Invarian §7, ditegakkan bukan didokumentasikan (P6): Σ tender = grand_total, EKSAK.
    const sum = sumStrings(tenders.map((t) => t.amount));
    const grandMoney = toMoney(grand);
    if (sum === undefined || grandMoney === undefined || !eqMoney(sum, grandMoney)) {
        return bad(e, `${path}.tenders`, 'tender_sum_mismatch');
    }
    // ⭐ `tax_total` = Σ `taxes[].tax_amount`, EKSAK — agregasi murni, nol kebijakan pajak.
    // `money.ts` (roundMoneyToDecimals) menyatakannya sebagai aturan: *"order.tax_total **wajib** =
    // sumMoney atas tax_amount per baris, tak pernah dihitung ulang dari subtotal × rate"* —
    // menghitung ulang di tingkat pesanan menghasilkan selisih 1–2 rupiah terhadap struk yang sudah
    // dicetak. Tanpa kontrol ini, `tax_total` ngawur lolos ke ERP/BI dan pecahnya baru terlihat saat
    // rekonsiliasi (P6: kontrol yang hanya didokumentasikan bukan kontrol).
    //
    // ⚠️ Baris ber-`kind_snapshot = 'service_charge'` SENGAJA dikecualikan: §6 mendaftar
    // `service_charge` dan `tax_total` sebagai dua komponen `grand_total` yang berbeda, sementara §8
    // memodelkan service charge sebagai `tax_code` (ia berperilaku sama dalam perhitungan).
    // Memasukkannya ke `tax_total` = menghitung ganda komponen yang sudah punya kolomnya sendiri.
    //
    // 🔶 Tak bertabrakan dengan §13 no.8 (urutan & dasar pengenaan bila PPN ditambah): yang
    // ditegakkan di sini hanya penjumlahan, bukan cara `tax_amount` dihitung.
    // Partisi SEKALI, lalu dua penjumlahan: §8 memodelkan service charge sebagai `tax_code` (ia
    // berperilaku sama — punya tarif, punya dasar, bisa kena pajak, berubah per outlet), sementara §6
    // mendaftarkan `service_charge` dan `tax_total` sebagai dua komponen `grand_total` yang BERBEDA.
    const scRows = taxes.filter((t) => t.kind_snapshot === 'service_charge');
    const taxRows = taxes.filter((t) => t.kind_snapshot !== 'service_charge');
    const taxSum = sumStrings(taxRows.map((t) => t.tax_amount));
    const taxTotalMoney = toMoney(taxTotal);
    if (taxSum === undefined || taxTotalMoney === undefined || !eqMoney(taxSum, taxTotalMoney)) {
        return bad(e, `${path}.tax_total`, 'tax_total_mismatch');
    }
    // ⭐ SIMETRIS dengan `tax_total` — dan simetri itu bukan kerapian, melainkan syarat.
    // Kalau hanya satu dari dua kolom rollup yang diverifikasi, yang tak diverifikasi adalah tempat
    // angka ngawur menumpuk: ia lolos ke ERP/BI dan baru terlihat saat rekonsiliasi, berbulan-bulan
    // kemudian (R2 §10b — kegagalan senyap terlihat persis seperti kesehatan).
    const scSum = sumStrings(scRows.map((t) => t.tax_amount));
    const serviceMoney = toMoney(service);
    if (scSum === undefined || serviceMoney === undefined || !eqMoney(scSum, serviceMoney)) {
        return bad(e, `${path}.service_charge_total`, 'service_charge_total_mismatch');
    }
    // ⭐ Identitas `grand_total` — **BERSYARAT pada `is_inclusive_snapshot`**, dan inilah alasan
    // kolom itu ada (§8 `tax_code.is_inclusive`, *"umum di F&B Indonesia"*).
    //
    //   grand_total = subtotal_gross − discount_total + Σ(baris pajak/SC yang EKSKLUSIF)
    //
    // Baris **inklusif** menyumbang nol pada penjumlahan ini: nilainya sudah berada di dalam
    // `subtotal_gross`. Menambahkannya lagi = menghitung ganda; mengabaikan kondisinya = memaksakan
    // satu rezim pajak ke seluruh tenant, persis asumsi terpahat yang P4 larang.
    // ⇒ Semua eksklusif  ⇒ identitas polos `subtotal − diskon + SC + pajak`.
    // ⇒ Semua inklusif   ⇒ `subtotal − diskon`.
    // ⇒ Campuran         ⇒ tetap benar, tanpa cabang khusus.
    //
    // ⚠️ **Batas yang diketahui:** payload belum punya `rounding_adjustment`. Bila kelak ada tenant
    // yang membulatkan `grand_total` ke rupiah/ratusan terdekat, identitas ini akan menolaknya — dan
    // jawabannya adalah **menambah field itu**, bukan melemahkan kontrol ini. Hari ini tak ada
    // payload sah yang bisa menyatakan pembulatan, jadi tak ada data benar yang ditolak.
    const subtotalMoney = toMoney(subtotal);
    const discountMoney = toMoney(discount);
    const exclusiveSum = sumStrings(taxes.filter((t) => !t.is_inclusive_snapshot).map((t) => t.tax_amount));
    if (subtotalMoney === undefined || discountMoney === undefined || exclusiveSum === undefined) {
        return bad(e, `${path}.grand_total`, 'money_invalid');
    }
    let expectedGrand;
    try {
        expectedGrand = addMoney(subMoney(subtotalMoney, discountMoney), exclusiveSum);
    }
    catch {
        // `range` dari money.ts — nilai di luar numeric(18,4). Tetap pelanggaran identitas, bukan crash.
        return bad(e, `${path}.grand_total`, 'grand_total_mismatch');
    }
    if (!eqMoney(expectedGrand, grandMoney)) {
        return bad(e, `${path}.grand_total`, 'grand_total_mismatch');
    }
    return Object.freeze({
        ref,
        order_type: orderType,
        entry_channel: entry,
        price_channel: price,
        subtotal_gross: subtotal,
        discount_total: discount,
        service_charge_total: service,
        tax_total: taxTotal,
        grand_total: grand,
        taxes: Object.freeze(taxes),
        tenders: Object.freeze(tenders),
        customer,
        closed_by: closedBy,
    });
}
function parseOrderVoided(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    noExtraKeys(obj, ['ref', 'reason', 'voided_after_send', 'priority_review', 'authorized_by', 'gross_reversed'], path, e);
    const rawRef = pick(obj, 'ref', path, e);
    const ref = rawRef === MISSING ? undefined : parseOrderRef(rawRef, `${path}.ref`, e);
    const rawReason = pick(obj, 'reason', path, e);
    const reason = rawReason === MISSING
        ? undefined
        : parseReasoned(rawReason, VOID_REASON_SET, `${path}.reason`, e);
    const afterSend = boolField(obj, 'voided_after_send', path, e);
    const priority = boolField(obj, 'priority_review', path, e);
    const rawAuth = pick(obj, 'authorized_by', path, e);
    const auth = rawAuth === MISSING ? undefined : parseActor(rawAuth, `${path}.authorized_by`, e);
    const gross = field(obj, 'gross_reversed', path, e, asMoney, 'money_invalid');
    if (ref === undefined ||
        reason === undefined ||
        afterSend === undefined ||
        priority === undefined ||
        auth === undefined ||
        gross === undefined) {
        return undefined;
    }
    // §10 (dari `log_actiondesp` VTEC): void sesudah dapur memasak WAJIB ditinjau. Kontrol yang
    // memaksa, bukan sekadar mencatat (P6) — bahan sudah terpakai, biayanya sudah nyata.
    if (afterSend && !priority) {
        return bad(e, `${path}.priority_review`, 'priority_review_required');
    }
    return Object.freeze({
        ref,
        reason: reason,
        voided_after_send: afterSend,
        priority_review: priority,
        authorized_by: auth,
        gross_reversed: gross,
    });
}
function parseOrderRefunded(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    noExtraKeys(obj, ['ref', 'refund_id', 'reason', 'authorized_by', 'amount_refunded', 'tenders_reversed'], path, e);
    const rawRef = pick(obj, 'ref', path, e);
    const ref = rawRef === MISSING ? undefined : parseOrderRef(rawRef, `${path}.ref`, e);
    const rid = field(obj, 'refund_id', path, e, refundId, 'uuid_v7_invalid');
    const rawReason = pick(obj, 'reason', path, e);
    const reason = rawReason === MISSING
        ? undefined
        : parseReasoned(rawReason, REFUND_REASON_SET, `${path}.reason`, e);
    const rawAuth = pick(obj, 'authorized_by', path, e);
    const auth = rawAuth === MISSING ? undefined : parseActor(rawAuth, `${path}.authorized_by`, e);
    const amount = field(obj, 'amount_refunded', path, e, asMoney, 'money_invalid');
    const rawTenders = arrayField(obj, 'tenders_reversed', path, e, 1);
    let reversed;
    if (rawTenders !== undefined) {
        const out = [];
        let okAll = true;
        for (let i = 0; i < rawTenders.length; i += 1) {
            const row = parseTenderReversed(rawTenders[i], `${path}.tenders_reversed[${String(i)}]`, e);
            if (row === undefined)
                okAll = false;
            else
                out.push(row);
        }
        reversed = okAll ? out : undefined;
    }
    if (ref === undefined ||
        rid === undefined ||
        reason === undefined ||
        auth === undefined ||
        amount === undefined ||
        reversed === undefined) {
        return undefined;
    }
    // Invarian §7 dalam arah sebaliknya: apa yang dikembalikan wajib sama dengan apa yang dibalik.
    const sum = sumStrings(reversed.map((t) => t.amount));
    const amountMoney = toMoney(amount);
    if (sum === undefined || amountMoney === undefined || !eqMoney(sum, amountMoney)) {
        return bad(e, `${path}.tenders_reversed`, 'tender_sum_mismatch');
    }
    return Object.freeze({
        ref,
        refund_id: rid,
        reason: reason,
        authorized_by: auth,
        amount_refunded: amount,
        tenders_reversed: Object.freeze(reversed),
    });
}
function parseShiftOpened(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    noExtraKeys(obj, ['ref', 'opening_float'], path, e);
    const rawRef = pick(obj, 'ref', path, e);
    const ref = rawRef === MISSING ? undefined : parseShiftRef(rawRef, `${path}.ref`, e);
    const float = fieldOrNull(obj, 'opening_float', path, e, asMoney, 'money_invalid');
    if (ref === undefined || float === undefined)
        return undefined;
    return Object.freeze({ ref, opening_float: float });
}
function parseShiftClosed(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    noExtraKeys(obj, ['ref', 'expected_cash', 'counted_cash', 'variance', 'variance_reason', 'approved_by'], path, e);
    const rawRef = pick(obj, 'ref', path, e);
    const ref = rawRef === MISSING ? undefined : parseShiftRef(rawRef, `${path}.ref`, e);
    const expected = field(obj, 'expected_cash', path, e, asMoney, 'money_invalid');
    const counted = field(obj, 'counted_cash', path, e, asMoney, 'money_invalid');
    const variance = field(obj, 'variance', path, e, asMoney, 'money_invalid');
    const rawReason = pick(obj, 'variance_reason', path, e);
    let reason;
    if (rawReason === MISSING)
        reason = undefined;
    else if (rawReason === null)
        reason = null;
    else
        reason = parseReasoned(rawReason, CASH_VARIANCE_REASON_SET, `${path}.variance_reason`, e);
    const rawApproved = pick(obj, 'approved_by', path, e);
    const approved = rawApproved === MISSING ? undefined : parseActorOrNull(rawApproved, `${path}.approved_by`, e);
    if (ref === undefined ||
        expected === undefined ||
        counted === undefined ||
        variance === undefined ||
        reason === undefined ||
        approved === undefined) {
        return undefined;
    }
    // P3 + P1: ketiganya dikirim, dan hubungannya diperiksa EKSAK sebagai bigint.
    const expectedM = toMoney(expected);
    const countedM = toMoney(counted);
    const varianceM = toMoney(variance);
    if (expectedM === undefined || countedM === undefined || varianceM === undefined) {
        return bad(e, `${path}.variance`, 'money_invalid');
    }
    let derived;
    try {
        derived = subMoney(countedM, expectedM);
    }
    catch {
        return bad(e, `${path}.variance`, 'variance_mismatch');
    }
    if (!eqMoney(derived, varianceM)) {
        return bad(e, `${path}.variance`, 'variance_mismatch');
    }
    // DESIGN §5b — selisih kas WAJIB beralasan. Bukti: `Remark` VTEC terisi 17 dari 8.125 hari.
    if (varianceM !== 0n && reason === null) {
        return bad(e, `${path}.variance_reason`, 'variance_reason_required');
    }
    return Object.freeze({
        ref,
        expected_cash: expected,
        counted_cash: counted,
        variance,
        variance_reason: reason === null ? null : reason,
        approved_by: approved,
    });
}
function parseBusinessDayClosed(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    noExtraKeys(obj, ['ref', 'shift_count', 'open_order_count', 'total_variance', 'approved_by'], path, e);
    const rawRef = pick(obj, 'ref', path, e);
    const ref = rawRef === MISSING ? undefined : parseDayRef(rawRef, `${path}.ref`, e);
    const shifts = intField(obj, 'shift_count', path, e, 0);
    const openOrders = intField(obj, 'open_order_count', path, e, 0);
    const variance = field(obj, 'total_variance', path, e, asMoney, 'money_invalid');
    const rawApproved = pick(obj, 'approved_by', path, e);
    const approved = rawApproved === MISSING ? undefined : parseActorOrNull(rawApproved, `${path}.approved_by`, e);
    if (ref === undefined ||
        shifts === undefined ||
        openOrders === undefined ||
        variance === undefined ||
        approved === undefined) {
        return undefined;
    }
    const varianceM = toMoney(variance);
    if (varianceM === undefined)
        return bad(e, `${path}.total_variance`, 'money_invalid');
    // ⭐ DESIGN §5b — tutup hari TIDAK BISA diselesaikan tanpa persetujuan penyelia bila ada selisih
    //    atau masih ada pesanan terbuka. Ambangnya setelan tenant (data); "≠ 0 atau > 0" adalah
    //    lantai yang tak bisa ditawar. Bukti: VTEC melewatkan 43 hari tanpa tutup, tanpa sinyal.
    if ((varianceM !== 0n || openOrders > 0) && approved === null) {
        return bad(e, `${path}.approved_by`, 'supervisor_approval_required');
    }
    return Object.freeze({
        ref,
        shift_count: shifts,
        open_order_count: openOrders,
        total_variance: variance,
        approved_by: approved,
    });
}
function parseStockMoved(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    noExtraKeys(obj, ['ref', 'movement_type', 'is_theoretical', 'qty', 'uom', 'reason', 'source_ref'], path, e);
    const rawRef = pick(obj, 'ref', path, e);
    const ref = rawRef === MISSING ? undefined : parseStockRef(rawRef, `${path}.ref`, e);
    const type = enumField(obj, 'movement_type', path, e, isMovementType);
    const theoretical = boolField(obj, 'is_theoretical', path, e);
    const quantity = field(obj, 'qty', path, e, asQty, 'qty_invalid');
    const uom = field(obj, 'uom', path, e, codeStr, 'code_invalid');
    const rawReason = pick(obj, 'reason', path, e);
    let reason;
    if (rawReason === MISSING)
        reason = undefined;
    else if (rawReason === null)
        reason = null;
    else
        reason = parseReasoned(rawReason, STOCK_REASON_SET, `${path}.reason`, e);
    const rawSource = pick(obj, 'source_ref', path, e);
    let source;
    if (rawSource === MISSING)
        source = undefined;
    else if (rawSource === null)
        source = null;
    else
        source = parseStockSourceRef(rawSource, `${path}.source_ref`, e);
    if (ref === undefined ||
        type === undefined ||
        theoretical === undefined ||
        quantity === undefined ||
        uom === undefined ||
        reason === undefined ||
        source === undefined) {
        return undefined;
    }
    // §9 — sifat teoretis DITENTUKAN jenisnya; dua sumber kebenaran akan menyimpang (P2).
    if (theoretical !== MOVEMENT_IS_THEORETICAL[type]) {
        return bad(e, `${path}.is_theoretical`, 'theoretical_flag_mismatch');
    }
    // P8 — pergerakan nol tak menggerakkan apa pun. Ini regresi langsung atas 135.593 dokumen
    // konsumsi bahan VTEC yang **92% kosong** (§9, §8j).
    const q = tryQty(quantity);
    if (q === undefined)
        return bad(e, `${path}.qty`, 'qty_invalid');
    if (isZeroQty(q))
        return bad(e, `${path}.qty`, 'qty_zero');
    // P8 — backflush tanpa resep = dokumen tanpa masukan ⇒ alarm cakupan, bukan peristiwa kosong.
    if (type === 'sale_backflush' && (source === null || source.kind !== 'recipe')) {
        return bad(e, `${path}.source_ref`, 'backflush_source_required');
    }
    // P2/P6 — selalu ada "kenapa" yang bisa dianalisis: enum untuk gerakan yang ditentukan manusia,
    // `source_ref` untuk gerakan yang alasannya struktural (lihat catatan 🔶 di StockMovedPayload).
    if (reason === null) {
        if (type !== 'receipt' && type !== 'sale_backflush') {
            return bad(e, `${path}.reason`, 'reason_code_unknown');
        }
        if (source === null)
            return bad(e, `${path}.source_ref`, 'backflush_source_required');
    }
    return Object.freeze({
        ref,
        movement_type: type,
        is_theoretical: theoretical,
        qty: quantity,
        uom,
        reason: reason === null ? null : reason,
        source_ref: source,
    });
}
function parseCustomerIdentified(v, path, e) {
    const obj = asRecord(v, path, e);
    if (obj === undefined)
        return undefined;
    noExtraKeys(obj, ['ref', 'identified_via', 'order_ref'], path, e);
    const rawRef = pick(obj, 'ref', path, e);
    const ref = rawRef === MISSING ? undefined : parseMemberRef(rawRef, `${path}.ref`, e);
    const via = enumField(obj, 'identified_via', path, e, isIdentifiedVia);
    const rawOrder = pick(obj, 'order_ref', path, e);
    let order;
    if (rawOrder === MISSING)
        order = undefined;
    else if (rawOrder === null)
        order = null;
    else
        order = parseOrderRef(rawOrder, `${path}.order_ref`, e);
    if (ref === undefined || via === undefined || order === undefined)
        return undefined;
    return Object.freeze({ ref, identified_via: via, order_ref: order });
}
const PAYLOAD_PARSERS = Object.freeze({
    'business_day.closed': parseBusinessDayClosed,
    'customer.identified': parseCustomerIdentified,
    'order.opened': parseOrderOpened,
    'order.paid': parseOrderPaid,
    'order.refunded': parseOrderRefunded,
    'order.sent': parseOrderSent,
    'order.voided': parseOrderVoided,
    'shift.closed': parseShiftClosed,
    'shift.opened': parseShiftOpened,
    'stock.moved': parseStockMoved,
});
const ENVELOPE_KEYS = [
    'event_id',
    'event_name',
    'schema_version',
    'tenant_code',
    'outlet_code',
    'occurred_at',
    'business_date',
    'emitted_by',
    'actor',
    'payload',
];
/**
 * ⭐ **Satu-satunya pintu masuk peristiwa dari kawat.**
 *
 * ### Fail-closed (P6) — dan kenapa
 * - **Tak pernah melempar.** Masukan apa pun (`null`, `[]`, `0`, string, objek bersarang dalam)
 *   menghasilkan `{ ok: false, errors }`, bukan pengecualian yang menjatuhkan konsumer batch.
 * - **Tidak memaafkan.** Field tak dikenal — di amplop maupun payload — **ditolak**.
 * - **`schema_version` lebih tinggi dari yang dikenal ⇒ ditolak** dengan kode `schema_too_new`,
 *   bukan diabaikan diam-diam. R2 §10b: kegagalan integrasi wajib **terlihat**; *integrasi yang
 *   berjalan tanpa menghasilkan apa pun terlihat persis seperti integrasi yang sehat*
 *   (bukti: 4.547 baris tak tersinkron + 719 error bertahun-tahun, tanpa ada yang tahu).
 *
 * ### Deterministik
 * Parser **tidak membangkitkan nilai apa pun** — tak ada `event_id` baru, tak ada stempel waktu,
 * tak ada default. Dua panggilan atas JSON identik menghasilkan nilai yang setara (SYNC §3:
 * *"pengiriman ulang batch yang sama = tanpa efek"*).
 *
 * ### Yang sengaja TIDAK diperiksa
 * Hubungan `business_date` ⇄ `occurred_at`. Transaksi 01:30 WIB masuk `business_date` hari
 * sebelumnya (§2) — memaksa keduanya "konsisten" justru akan menolak data yang benar.
 */
export function parseEvent(input) {
    const errors = [];
    const path = '$';
    const obj = asRecord(input, path, errors);
    if (obj === undefined)
        return { ok: false, errors: Object.freeze(errors) };
    noExtraKeys(obj, ENVELOPE_KEYS, path, errors);
    const rawName = pick(obj, 'event_name', path, errors);
    if (rawName === MISSING)
        return { ok: false, errors: Object.freeze(errors) };
    if (!isEventName(rawName)) {
        bad(errors, `${path}.event_name`, 'event_name_unknown');
        return { ok: false, errors: Object.freeze(errors) };
    }
    const name = rawName;
    const id = field(obj, 'event_id', path, errors, eventId, 'uuid_v7_invalid');
    const tenant = field(obj, 'tenant_code', path, errors, tenantCode, 'code_invalid');
    const outlet = field(obj, 'outlet_code', path, errors, outletCode, 'code_invalid');
    const occurred = field(obj, 'occurred_at', path, errors, utcTimestamp, 'timestamp_invalid');
    const bdate = field(obj, 'business_date', path, errors, businessDate, 'business_date_invalid');
    const rawVersion = pick(obj, 'schema_version', path, errors);
    let version;
    if (rawVersion !== MISSING) {
        if (typeof rawVersion !== 'number' || !Number.isSafeInteger(rawVersion) || rawVersion < 1) {
            bad(errors, `${path}.schema_version`, 'schema_version_invalid');
        }
        else if (rawVersion > EVENTS_SCHEMA_VERSION[name]) {
            bad(errors, `${path}.schema_version`, 'schema_too_new');
        }
        else {
            version = rawVersion;
        }
    }
    const rawEmitted = pick(obj, 'emitted_by', path, errors);
    const emitted = rawEmitted === MISSING ? undefined : parseEmittedBy(rawEmitted, `${path}.emitted_by`, errors);
    const rawActor = pick(obj, 'actor', path, errors);
    const actor = rawActor === MISSING ? undefined : parseActor(rawActor, `${path}.actor`, errors);
    const rawPayload = pick(obj, 'payload', path, errors);
    const payload = rawPayload === MISSING
        ? undefined
        : PAYLOAD_PARSERS[name](rawPayload, `${path}.payload`, errors);
    if (errors.length > 0 ||
        id === undefined ||
        version === undefined ||
        tenant === undefined ||
        outlet === undefined ||
        occurred === undefined ||
        bdate === undefined ||
        emitted === undefined ||
        actor === undefined ||
        payload === undefined) {
        return { ok: false, errors: Object.freeze(errors) };
    }
    // Cast tunggal & terkurung: setiap komponen sudah divalidasi oleh parser bertipe di atas, dan
    // `payload` datang dari PAYLOAD_PARSERS yang diindeks oleh `name` yang sama.
    const value = Object.freeze({
        event_id: id,
        event_name: name,
        schema_version: version,
        tenant_code: tenant,
        outlet_code: outlet,
        occurred_at: occurred,
        business_date: bdate,
        emitted_by: emitted,
        actor,
        payload,
    });
    return { ok: true, value };
}
/**
 * Penyempit tipe: apakah peristiwa ini bernama `name`? Menghindari `as` di sisi konsumen.
 *
 * ```ts
 * if (isEventOf(ev, 'order.paid')) ev.payload.grand_total; // bertipe MoneyString
 * ```
 */
export function isEventOf(event, name) {
    return event.event_name === name;
}
//# sourceMappingURL=events.js.map